import { useEffect, useRef, useState } from 'react';

/**
 * Wall-clock-based count-up timer for the scenario scoring flow.
 *
 * The existing BLE timer source (`./sg.ts`) drives count-DOWN par
 * timing — useful for ÖSB rapid-fire and similar disciplines where the
 * referee picks a par and a buzzer marks the deadline. Scenario scoring
 * is the inverse: time starts at 0 on the start signal, ticks up while
 * the shooter works the scenario, and stops on the last shot. The metric
 * is the elapsed time, not "did you finish under par".
 *
 * Implementation is intentionally minimal:
 *   - Owned state is `startedAt` (epoch ms when running) + `stoppedAt`
 *     (epoch ms when stopped). Pause/resume not needed for scenarios.
 *   - The hook re-renders ~10 times per second while running so the
 *     visible counter looks smooth. RAF is overkill (the eye reads
 *     hundredths of seconds, not 60 fps), and a 100 ms interval keeps
 *     CPU near zero on low-end Android tablets.
 *   - Subscribers can read `elapsedMs()` for the precise current value
 *     (e.g. when stamping a shot's `elapsed_time_ms` on stop).
 */
export interface CountUpTimer {
  /** True between start() and stop(). */
  running: boolean;
  /** Most recent elapsed reading in ms. Updates on every tick. */
  elapsedMs: number;
  /** Wall-clock start, or null when never started. */
  startedAt: number | null;
  /** Wall-clock stop, or null when still running / never started. */
  stoppedAt: number | null;
  start(): void;
  stop(): void;
  reset(): void;
}

export function useCountUpTimer(opts: { tickMs?: number } = {}): CountUpTimer {
  const tickMs = opts.tickMs ?? 100;
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [stoppedAt, setStoppedAt] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (startedAt == null || stoppedAt != null) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => setNow(Date.now()), tickMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [startedAt, stoppedAt, tickMs]);

  const running = startedAt != null && stoppedAt == null;
  const elapsedMs =
    startedAt == null ? 0 : (stoppedAt ?? now) - startedAt;

  return {
    running,
    elapsedMs,
    startedAt,
    stoppedAt,
    start() {
      const t = Date.now();
      setStartedAt(t);
      setStoppedAt(null);
      setNow(t);
    },
    stop() {
      if (startedAt != null && stoppedAt == null) setStoppedAt(Date.now());
    },
    reset() {
      setStartedAt(null);
      setStoppedAt(null);
    },
  };
}

/**
 * Format a millisecond duration as `M:SS.ss` (scenario-style),
 * e.g. 1750 ms → "0:01.75". Used by the count-up display.
 */
export function formatCountUp(ms: number): string {
  const totalCs = Math.max(0, Math.floor(ms / 10));
  const cs = totalCs % 100;
  const totalSec = Math.floor(totalCs / 100);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60);
  return `${min}:${sec.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}
